import React, { useState, useEffect } from 'react';

const Requisition = ({ onNavigate, userInfo }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [requisitionModalOpen, setRequisitionModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [itemName, setItemName] = useState('');
    const [quantity, setQuantity] = useState('1');
    const [description, setDescription] = useState('');
    const [urgency, setUrgency] = useState('normal');

    // State for data from database
    const [requisitions, setRequisitions] = useState([]);
    const [statsData, setStatsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [userName, setUserName] = useState('');

    // API Base URL
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in/api';

    const categories = [
        { id: 'furniture', name: 'Furniture', icon: '🪑' },
        { id: 'electronics', name: 'Electronics', icon: '📱' },
        { id: 'bedding', name: 'Bedding', icon: '🛏️' },
        { id: 'maintenance', name: 'Maintenance', icon: '🔧' },
        { id: 'stationary', name: 'Stationary', icon: '✏️' },
        { id: 'other', name: 'Other', icon: '📦' }
    ];

    // Fetch user name and requisitions data from backend
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                const userId = userInfo?.userId || userInfo?.id;

                if (!userId) {
                    throw new Error('User ID not found');
                }

                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const headers = {
                    'Content-Type': 'application/json',
                };

                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                // Fetch user profile
                try {
                    const profileResponse = await fetch(`${API_BASE_URL}/student/profile/${userId}`, {
                        method: 'GET',
                        headers: headers
                    });

                    if (profileResponse.ok) {
                        const profileData = await profileResponse.json();
                        if (profileData.success && profileData.data) {
                            setUserName(profileData.data.fullName || 'User');
                        }
                    }
                } catch (err) {
                    console.warn('Could not fetch user profile:', err);
                }

                // Fetch requisitions
                const requisitionsResponse = await fetch(`${API_BASE_URL}/student/requisitions/${userId}`, {
                    method: 'GET',
                    headers: headers
                });

                if (!requisitionsResponse.ok) {
                    throw new Error('Failed to fetch requisitions');
                }

                const requisitionsData = await requisitionsResponse.json();
                
                if (requisitionsData.success) {
                    setRequisitions(requisitionsData.requisitions || []);
                } else {
                    console.warn('Failed to fetch requisitions:', requisitionsData.message);
                    setRequisitions([]);
                }

                // Fetch requisition stats
                const statsResponse = await fetch(`${API_BASE_URL}/student/requisitions-stats/${userId}`, {
                    method: 'GET',
                    headers: headers
                });

                if (statsResponse.ok) {
                    const statsResult = await statsResponse.json();
                    if (statsResult.success && statsResult.stats) {
                        const statsInfo = statsResult.stats;
                        setStatsData([
                            { 
                                icon: '📊', 
                                label: 'Total Requests', 
                                value: statsInfo.total.toString(), 
                                subtext: 'All time' 
                            },
                            { 
                                icon: '✅', 
                                label: 'Approved', 
                                value: statsInfo.approved.toString(), 
                                subtext: 'Successfully approved' 
                            },
                            { 
                                icon: '⏳', 
                                label: 'Pending', 
                                value: statsInfo.pending.toString(), 
                                subtext: 'Awaiting approval' 
                            },
                            { 
                                icon: '🔄', 
                                label: 'In Progress', 
                                value: statsInfo.inProgress.toString(), 
                                subtext: 'Being processed' 
                            }
                        ]);
                    }
                } else {
                    setStatsData([
                        { icon: '📊', label: 'Total Requests', value: '0', subtext: 'All time' },
                        { icon: '✅', label: 'Approved', value: '0', subtext: 'Successfully approved' },
                        { icon: '⏳', label: 'Pending', value: '0', subtext: 'Awaiting approval' },
                        { icon: '🔄', label: 'In Progress', value: '0', subtext: 'Being processed' }
                    ]);
                }

            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [userInfo, API_BASE_URL]);

    const stats = statsData || [
        { icon: '📊', label: 'Total Requests', value: '0', subtext: 'All time' },
        { icon: '✅', label: 'Approved', value: '0', subtext: 'Successfully approved' },
        { icon: '⏳', label: 'Pending', value: '0', subtext: 'Awaiting approval' },
        { icon: '🔄', label: 'In Progress', value: '0', subtext: 'Being processed' }
    ];

    const handleSubmitRequisition = async () => {
        if (!selectedCategory || !itemName || !quantity) {
            alert('Please fill in all required fields');
            return;
        }

        try {
            setSubmitting(true);

            const userId = userInfo?.userId || userInfo?.id;
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');

            const headers = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${API_BASE_URL}/student/requisitions/${userId}`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                    itemName: itemName,
                    category: selectedCategory,
                    quantity: parseInt(quantity),
                    description: description,
                    urgency: urgency
                })
            });

            if (!response.ok) {
                throw new Error('Failed to submit requisition');
            }

            const result = await response.json();

            if (result.success) {
                alert(`Requisition submitted successfully!\n\nRequisition Number: ${result.requisitionNumber}\n\nYour request is pending approval.`);

                // Reset form
                setSelectedCategory('');
                setItemName('');
                setQuantity('1');
                setDescription('');
                setUrgency('normal');
                setRequisitionModalOpen(false);

                // Refresh data
                const requisitionsResponse = await fetch(`${API_BASE_URL}/student/requisitions/${userId}`, {
                    method: 'GET',
                    headers: headers
                });

                if (requisitionsResponse.ok) {
                    const requisitionsData = await requisitionsResponse.json();
                    if (requisitionsData.success) {
                        setRequisitions(requisitionsData.requisitions || []);
                    }
                }

                // Refresh stats
                const statsResponse = await fetch(`${API_BASE_URL}/student/requisitions-stats/${userId}`, {
                    method: 'GET',
                    headers: headers
                });

                if (statsResponse.ok) {
                    const statsResult = await statsResponse.json();
                    if (statsResult.success && statsResult.stats) {
                        const statsInfo = statsResult.stats;
                        setStatsData([
                            { icon: '📊', label: 'Total Requests', value: statsInfo.total.toString(), subtext: 'All time' },
                            { icon: '✅', label: 'Approved', value: statsInfo.approved.toString(), subtext: 'Successfully approved' },
                            { icon: '⏳', label: 'Pending', value: statsInfo.pending.toString(), subtext: 'Awaiting approval' },
                            { icon: '🔄', label: 'In Progress', value: statsInfo.inProgress.toString(), subtext: 'Being processed' }
                        ]);
                    }
                }
            } else {
                alert(result.message || 'Failed to submit requisition');
            }

        } catch (error) {
            console.error('Error submitting requisition:', error);
            alert('Failed to submit requisition. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const getStatusColor = (status) => {
        switch(status) {
            case 'approved': return 'approved';
            case 'rejected': return 'rejected';
            case 'pending': return 'pending';
            case 'in-progress': return 'in-progress';
            default: return 'pending';
        }
    };

    const getStatusIcon = (status) => {
        switch(status) {
            case 'approved': return '✅';
            case 'rejected': return '❌';
            case 'pending': return '⏳';
            case 'in-progress': return '🔄';
            default: return '⏳';
        }
    };

    const MenuItem = ({ icon, label, active = false, route }) => (
        <div
            className={`menu-item ${active ? 'menu-item-active' : ''}`}
            onClick={() => {
                if (route && onNavigate) {
                    onNavigate(route);
                }
                setSidebarOpen(false);
            }}
        >
            <span className="menu-icon">{icon}</span>
            <span className="menu-label">{label}</span>
            <div className="menu-glow"></div>
        </div>
    );

    const handleLogout = () => {
        if (onNavigate) {
            onNavigate('hostel-login');
        }
    };

    const displayName = userName || userInfo?.name || userInfo?.fullName || 'Student';
    const displayInitial = displayName.charAt(0).toUpperCase();

    // Loading State
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#ffffff',
                flexDirection: 'column',
                gap: '20px'
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    border: '6px solid #f3f3f3',
                    borderTop: '6px solid #2b6777',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{
                    color: '#2b6777',
                    fontSize: '18px',
                    fontWeight: '600'
                }}>Loading Requisitions...</p>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f4f7fa',
                padding: '2rem'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    padding: '3rem',
                    textAlign: 'center',
                    maxWidth: '500px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
                    <h3 style={{ color: '#dc2626', marginBottom: '1rem', fontSize: '1.5rem' }}>
                        Error Loading Requisitions
                    </h3>
                    <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            backgroundColor: '#2b6777',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.75rem 1.5rem',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: '600'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    -webkit-font-smoothing: antialiased;
                    overflow-x: hidden;
                }

                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes shimmer {
                    0% { background-position: -1000px 0; }
                    100% { background-position: 1000px 0; }
                }

                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }

                @keyframes glow {
                    0%, 100% { opacity: 0.5; }
                    50% { opacity: 1; }
                }

                .dashboard-container {
                    display: flex;
                    height: 100vh;
                    background: #ffffff;
                    position: relative;
                    overflow: hidden;
                }

                /* Sidebar */
                .sidebar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    width: 300px;
                    background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(242,242,242,0.98) 100%);
                    backdrop-filter: blur(30px);
                    border-right: 2px solid rgba(82,171,152,0.2);
                    transform: translateX(-100%);
                    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    z-index: 100;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 8px 0 40px rgba(43,103,119,0.15);
                }

                .sidebar.open {
                    transform: translateX(0);
                }

                .sidebar-header {
                    padding: 36px 28px;
                    background: linear-gradient(135deg, rgba(43,103,119,0.05) 0%, rgba(82,171,152,0.05) 100%);
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                    position: relative;
                    overflow: hidden;
                }

                .sidebar-header::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(82,171,152,0.15), transparent);
                    animation: shimmer 4s infinite;
                }

                .sidebar-top {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    margin-bottom: 24px;
                    position: relative;
                    z-index: 1;
                }

                .close-btn {
                    background: rgba(43,103,119,0.08);
                    border: 1.5px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 20px;
                    color: #2b6777;
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .close-btn:hover {
                    background: rgba(43,103,119,0.15);
                    border-color: #52ab98;
                    transform: rotate(90deg);
                }

                .user-info-sidebar {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    position: relative;
                    z-index: 1;
                }

                .user-avatar-sidebar {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 22px;
                    color: white;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                    border: 3px solid rgba(255, 255, 255, 0.5);
                }

                .user-details-sidebar {
                    flex: 1;
                }

                .user-name-sidebar {
                    font-size: 16px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 4px;
                }

                .user-role-sidebar {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                }

                .sidebar-nav {
                    flex: 1;
                    padding: 28px 20px;
                    overflow-y: auto;
                }

                .sidebar-nav::-webkit-scrollbar {
                    width: 6px;
                }

                .sidebar-nav::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .sidebar-nav::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 3px;
                }

                .menu-item {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 16px 18px;
                    margin-bottom: 10px;
                    cursor: pointer;
                    color: #2b6777;
                    font-size: 15px;
                    font-weight: 600;
                    border-radius: 14px;
                    transition: all 0.3s;
                    overflow: hidden;
                }

                .menu-glow {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    opacity: 0;
                    transition: opacity 0.3s;
                }

                .menu-item:hover {
                    color: #52ab98;
                    background: rgba(82,171,152,0.1);
                    transform: translateX(6px);
                    box-shadow: 0 4px 16px rgba(82,171,152,0.15);
                }

                .menu-item:hover .menu-glow {
                    opacity: 1;
                }

                .menu-item-active {
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                    color: #2b6777;
                    box-shadow: 0 6px 20px rgba(82,171,152,0.2);
                    font-weight: 700;
                }

                .menu-item-active .menu-glow {
                    opacity: 1;
                    animation: glow 2s ease-in-out infinite;
                }

                .menu-icon {
                    font-size: 22px;
                    position: relative;
                    z-index: 1;
                }

                .menu-label {
                    position: relative;
                    z-index: 1;
                }

                .logout-section {
                    padding: 20px;
                    border-top: 2px solid rgba(82,171,152,0.15);
                }

                .logout-btn {
                    width: 100%;
                    padding: 16px 20px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 14px;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .logout-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 35px rgba(43,103,119,0.4);
                }

                /* Overlay */
                .overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(43,103,119,0.4);
                    backdrop-filter: blur(6px);
                    z-index: 90;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.3s ease;
                }

                .overlay.show {
                    opacity: 1;
                    visibility: visible;
                }

                /* Main Content */
                .main-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    width: 100%;
                    position: relative;
                    z-index: 1;
                }

                /* Top Header */
                .top-header {
                    background: #ffffff;
                    padding: 24px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid rgba(82,171,152,0.12);
                    position: relative;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .top-header::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex: 1;
                }

                .menu-toggle {
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border: 2px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 24px;
                    color: #2b6777;
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                    box-shadow: 0 4px 20px rgba(43,103,119,0.15);
                    flex-shrink: 0;
                }

                .menu-toggle:hover {
                    transform: scale(1.05);
                    box-shadow: 0 6px 30px rgba(43,103,119,0.25);
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                }

                .header-title-section {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 0;
                    flex: 1;
                }

                .header-greeting {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                    display: none;
                }

                .header-name {
                    font-size: 18px;
                    font-weight: 900;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .header-right {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-shrink: 0;
                }

                .user-avatar-header {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 18px;
                    color: white;
                    cursor: pointer;
                    border: 3px solid rgba(82,171,152,0.3);
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .user-avatar-header:hover {
                    transform: scale(1.1);
                    box-shadow: 0 8px 32px rgba(43,103,119,0.5);
                }

                /* Content Wrapper */
                .content-wrapper {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px 16px;
                }

                .content-wrapper::-webkit-scrollbar {
                    width: 8px;
                }

                .content-wrapper::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .content-wrapper::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 4px;
                }

                .content-area {
                    max-width: 1500px;
                    margin: 0 auto;
                    animation: slideIn 0.6s ease-out;
                }

                /* Hero Section */
                .hero-section {
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    border-radius: 12px;
                    padding: 28px 20px;
                    margin-bottom: 24px;
                    border: 1px solid rgba(82,171,152,0.15);
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                    position: relative;
                    overflow: hidden;
                }

                .hero-section::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .hero-content {
                    position: relative;
                    z-index: 1;
                }

                .hero-title {
                    font-size: 24px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 8px;
                    letter-spacing: -0.5px;
                }

                .hero-subtitle {
                    font-size: 13px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 500;
                }

                /* Stats Grid */
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                    margin-bottom: 24px;
                }

                .stat-card {
                    background: #ffffff;
                    border-radius: 12px;
                    padding: 20px 16px;
                    border: 1px solid rgba(43,103,119,0.12);
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .stat-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 4px;
                    height: 100%;
                    background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
                    transform: scaleY(0);
                    transition: transform 0.3s ease;
                }

                .stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.12);
                    border-color: rgba(82,171,152,0.25);
                }

                .stat-card:hover::before {
                    transform: scaleY(1);
                }

                .stat-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 16px;
                }

                .stat-label {
                    font-size: 10px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .stat-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    background: rgba(82,171,152,0.08);
                    border: 1px solid rgba(82,171,152,0.15);
                }

                .stat-value {
                    font-size: 26px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 6px;
                    letter-spacing: -0.5px;
                    line-height: 1;
                }

                .stat-subtext {
                    font-size: 11px;
                    color: rgba(43,103,119,0.5);
                    font-weight: 500;
                }

                /* Section */
                .section {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 24px 20px;
                    margin-bottom: 24px;
                    border: 2px solid rgba(82,171,152,0.2);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.08);
                }

                .section-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 20px;
                    padding-bottom: 16px;
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                    flex-wrap: wrap;
                    gap: 12px;
                }

                .section-title-group {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .section-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border: 2px solid rgba(82,171,152,0.3);
                    color: white;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.2);
                    flex-shrink: 0;
                }

                .section-title {
                    font-size: 18px;
                    font-weight: 900;
                    color: #2b6777;
                }

                .new-request-btn {
                    padding: 12px 24px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.3s;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.3);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .new-request-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.4);
                }

                /* Requisitions Grid */
                .requisitions-grid {
                    display: grid;
                    gap: 16px;
                }

                .requisition-card {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 20px;
                    border: 2px solid rgba(82,171,152,0.2);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.08);
                    transition: all 0.3s;
                }

                .requisition-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.12);
                    border-color: rgba(82,171,152,0.3);
                }

                .requisition-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 12px;
                    gap: 12px;
                }

                .requisition-main-info {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    flex: 1;
                }

                .requisition-details {
                    flex: 1;
                }

                .requisition-title {
                    font-size: 16px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 4px;
                }

                .requisition-category {
                    display: inline-block;
                    padding: 4px 10px;
                    border-radius: 8px;
                    font-size: 11px;
                    font-weight: 700;
                    background: rgba(82,171,152,0.15);
                    color: #2b6777;
                    margin-left: 8px;
                }

                .requisition-meta {
                    display: flex;
                    gap: 16px;
                    font-size: 12px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                    flex-wrap: wrap;
                    margin-bottom: 8px;
                }

                .requisition-description {
                    font-size: 14px;
                    color: rgba(43,103,119,0.7);
                    font-weight: 500;
                    margin-bottom: 16px;
                    line-height: 1.5;
                }

                .requisition-status {
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    white-space: nowrap;
                }

                .status-pending {
                    background: rgba(255,193,7,0.1);
                    color: #f57c00;
                }

                .status-in-progress {
                    background: rgba(33,150,243,0.1);
                    color: #2196f3;
                }

                .status-approved {
                    background: rgba(76,175,80,0.1);
                    color: #4caf50;
                }

                .status-rejected {
                    background: rgba(244,67,54,0.1);
                    color: #f44336;
                }

                .urgency-badge {
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 10px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .urgency-low {
                    background: rgba(76,175,80,0.1);
                    color: #4caf50;
                }

                .urgency-normal {
                    background: rgba(255,193,7,0.1);
                    color: #f57c00;
                }

                .urgency-high {
                    background: rgba(244,67,54,0.1);
                    color: #f44336;
                }

                .requisition-footer {
                    margin-top: 16px;
                    padding-top: 12px;
                    border-top: 1px solid rgba(82,171,152,0.15);
                }

                .approval-info {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                    margin-bottom: 12px;
                }

                .approval-row {
                    display: flex;
                    gap: 8px;
                    font-size: 12px;
                }

                .approval-label {
                    color: rgba(43,103,119,0.5);
                    font-weight: 700;
                }

                .approval-value {
                    color: #2b6777;
                    font-weight: 800;
                }

                .remarks-box {
                    background: rgba(82,171,152,0.05);
                    padding: 12px;
                    border-radius: 8px;
                    border: 1px solid rgba(82,171,152,0.15);
                }

                .remarks-label {
                    font-size: 11px;
                    color: rgba(43,103,119,0.5);
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 6px;
                }

                .remarks-text {
                    font-size: 13px;
                    color: #2b6777;
                    font-weight: 600;
                    line-height: 1.5;
                }

                .empty-state {
                    text-align: center;
                    padding: 60px 20px;
                    color: rgba(43,103,119,0.5);
                }

                .empty-icon {
                    font-size: 48px;
                    margin-bottom: 16px;
                }

                .empty-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 8px;
                }

                .empty-text {
                    font-size: 14px;
                    color: rgba(43,103,119,0.6);
                }

                /* Modal */
                .requisition-modal {
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%) scale(0.9);
                    width: 90%;
                    max-width: 600px;
                    max-height: 90vh;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 20px 60px rgba(43,103,119,0.3);
                    z-index: 1001;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.3s;
                    display: flex;
                    flex-direction: column;
                }

                .requisition-modal.open {
                    opacity: 1;
                    visibility: visible;
                    transform: translate(-50%, -50%) scale(1);
                }

                .modal-header {
                    padding: 24px;
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                }

                .modal-title {
                    font-size: 20px;
                    font-weight: 900;
                    color: #2b6777;
                }

                .modal-close {
                    background: rgba(43,103,119,0.08);
                    border: 1.5px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 18px;
                    color: #2b6777;
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .modal-close:hover {
                    background: rgba(43,103,119,0.15);
                    border-color: #52ab98;
                }

                .modal-content {
                    padding: 24px;
                    overflow-y: auto;
                    flex: 1;
                }

                .modal-content::-webkit-scrollbar {
                    width: 6px;
                }

                .modal-content::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .modal-content::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 3px;
                }

                .form-section {
                    margin-bottom: 20px;
                }

                .form-label {
                    display: block;
                    font-size: 13px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 8px;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .form-label.required::after {
                    content: '*';
                    color: #f44336;
                    margin-left: 4px;
                }

                .categories-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 10px;
                }

                .category-option {
                    padding: 12px;
                    background: rgba(82,171,152,0.05);
                    border-radius: 10px;
                    border: 2px solid rgba(82,171,152,0.15);
                    cursor: pointer;
                    transition: all 0.3s;
                    text-align: center;
                }

                .category-option:hover {
                    background: rgba(82,171,152,0.1);
                    border-color: rgba(82,171,152,0.3);
                }

                .category-option.selected {
                    background: rgba(82,171,152,0.15);
                    border-color: #52ab98;
                    box-shadow: 0 4px 16px rgba(82,171,152,0.2);
                }

                .category-icon {
                    font-size: 24px;
                    margin-bottom: 6px;
                }

                .category-name {
                    font-size: 12px;
                    font-weight: 700;
                    color: #2b6777;
                }

                .form-input,
                .form-textarea,
                .form-select {
                    width: 100%;
                    padding: 12px;
                    border: 2px solid rgba(82,171,152,0.2);
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #2b6777;
                    background: rgba(82,171,152,0.05);
                    transition: all 0.3s;
                    font-family: 'Inter', sans-serif;
                }

                .form-input:focus,
                .form-textarea:focus,
                .form-select:focus {
                    outline: none;
                    border-color: #52ab98;
                    background: rgba(82,171,152,0.1);
                    box-shadow: 0 4px 16px rgba(82,171,152,0.15);
                }

                .form-textarea {
                    min-height: 80px;
                    resize: vertical;
                }

                .urgency-options {
                    display: flex;
                    gap: 10px;
                }

                .urgency-option {
                    flex: 1;
                    padding: 12px;
                    background: rgba(82,171,152,0.05);
                    border-radius: 10px;
                    border: 2px solid rgba(82,171,152,0.15);
                    cursor: pointer;
                    transition: all 0.3s;
                    text-align: center;
                    font-size: 13px;
                    font-weight: 700;
                    color: #2b6777;
                }

                .urgency-option:hover {
                    background: rgba(82,171,152,0.1);
                    border-color: rgba(82,171,152,0.3);
                }

                .urgency-option.selected {
                    background: rgba(82,171,152,0.15);
                    border-color: #52ab98;
                    box-shadow: 0 4px 16px rgba(82,171,152,0.2);
                }

                .modal-footer {
                    padding: 20px 24px;
                    border-top: 2px solid rgba(82,171,152,0.15);
                    display: flex;
                    gap: 12px;
                }

                .modal-btn {
                    flex: 1;
                    padding: 12px 20px;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 700;
                    cursor: pointer;
                    transition: all 0.3s;
                }

                .cancel-btn {
                    background: rgba(82,171,152,0.1);
                    color: #2b6777;
                    border: 2px solid rgba(82,171,152,0.25);
                }

                .cancel-btn:hover {
                    background: rgba(82,171,152,0.2);
                    border-color: #52ab98;
                }

                .submit-btn {
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.3);
                }

                .submit-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(43,103,119,0.4);
                }

                .submit-btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                /* Responsive Design */
                @media (min-width: 768px) {
                    .top-header {
                        padding: 32px 24px;
                    }

                    .header-name {
                        font-size: 22px;
                    }

                    .header-greeting {
                        display: block;
                        font-size: 14px;
                    }

                    .header-title-section {
                        flex-direction: row;
                        align-items: center;
                        gap: 12px;
                    }

                    .content-wrapper {
                        padding: 32px 24px;
                    }

                    .hero-section {
                        padding: 48px 28px;
                    }

                    .hero-title {
                        font-size: 32px;
                    }

                    .hero-subtitle {
                        font-size: 15px;
                    }

                    .stats-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 16px;
                    }

                    .stat-card {
                        padding: 24px 20px;
                    }

                    .section {
                        padding: 32px 28px;
                    }

                    .section-header {
                        gap: 16px;
                        margin-bottom: 28px;
                    }

                    .section-icon {
                        width: 56px;
                        height: 56px;
                        font-size: 28px;
                    }

                    .section-title {
                        font-size: 22px;
                    }

                    .requisitions-grid {
                        gap: 20px;
                    }

                    .requisition-card {
                        padding: 24px;
                    }

                    .categories-grid {
                        grid-template-columns: repeat(3, 1fr);
                    }
                }

                @media (min-width: 1024px) {
                    .sidebar {
                        position: static;
                        transform: translateX(0);
                    }

                    .close-btn {
                        display: none;
                    }

                    .overlay {
                        display: none !important;
                    }

                    .overlay.modal-overlay {
                        display: block !important;
                    }

                    .menu-toggle {
                        display: none;
                    }

                    .top-header {
                        padding: 52px 32px;
                    }

                    .header-name {
                        font-size: 28px;
                    }

                    .header-greeting {
                        font-size: 18px;
                    }

                    .content-wrapper {
                        padding: 56px 48px;
                    }

                    .hero-section {
                        padding: 90px 36px;
                    }

                    .hero-title {
                        font-size: 36px;
                    }

                    .hero-subtitle {
                        font-size: 16px;
                    }

                    .stats-grid {
                        gap: 20px;
                    }

                    .stat-card {
                        padding: 28px;
                    }

                    .stat-label {
                        font-size: 12px;
                    }

                    .stat-icon {
                        width: 48px;
                        height: 48px;
                        font-size: 24px;
                    }

                    .stat-value {
                        font-size: 32px;
                    }

                    .stat-subtext {
                        font-size: 13px;
                    }

                    .section {
                        padding: 40px;
                        border-radius: 28px;
                    }

                    .section-header {
                        gap: 20px;
                        margin-bottom: 32px;
                        padding-bottom: 24px;
                    }

                    .section-icon {
                        width: 64px;
                        height: 64px;
                        border-radius: 18px;
                        font-size: 32px;
                    }

                    .section-title {
                        font-size: 28px;
                    }

                    .new-request-btn {
                        padding: 14px 32px;
                        font-size: 15px;
                    }

                    .requisition-card {
                        padding: 28px;
                        border-radius: 28px;
                    }

                    .requisition-title {
                        font-size: 18px;
                    }

                    .requisition-description {
                        font-size: 15px;
                    }

                    .requisition-meta {
                        font-size: 13px;
                    }

                    .modal-header {
                        padding: 32px;
                    }

                    .modal-title {
                        font-size: 24px;
                    }

                    .modal-content {
                        padding: 32px;
                    }

                    .modal-footer {
                        padding: 24px 32px;
                    }
                }

                @media (max-width: 380px) {
                    .header-name {
                        font-size: 16px;
                    }

                    .hero-title {
                        font-size: 20px;
                    }

                    .stat-value {
                        font-size: 22px;
                    }

                    .section-title {
                        font-size: 16px;
                    }

                    .new-request-btn {
                        padding: 10px 16px;
                        font-size: 13px;
                    }

                    .modal-footer {
                        flex-direction: column;
                    }

                    .urgency-options {
                        flex-direction: column;
                    }
                }
            `}</style>

            <div className="dashboard-container">
                {/* Sidebar */}
                <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <div className="sidebar-top">
                            <button onClick={() => setSidebarOpen(false)} className="close-btn">
                                ✕
                            </button>
                        </div>
                        <div className="user-info-sidebar">
                            <div className="user-avatar-sidebar">
                                {displayInitial}
                            </div>
                            <div className="user-details-sidebar">
                                <div className="user-name-sidebar">{displayName}</div>
                                <div className="user-role-sidebar">Student Portal</div>
                            </div>
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        <MenuItem icon="🏠" label="Dashboard" route="hostel-student-dashboard" />
                        <MenuItem icon="🚪" label="Entry/Exit Log" route="hostel-student-entry-exit-log" />
                        <MenuItem icon="🛏" label="Room Details" route="hostel-student-room-details" />
                        <MenuItem icon="🍽️" label="Food Preference" route="hostel-student-food-preference" />
                        <MenuItem icon="🧺" label="Laundry Service" route="hostel-student-laundry" />
                        <MenuItem icon="💳" label="Grievance" route="hostel-student-payments" />
                        <MenuItem icon="📝" label="Requests" route="hostel-student-requests" />
                        <MenuItem icon="📋" label="Requisition" active={true} route="hostel-student-requisition" />
                    </nav>

                    <div className="logout-section">
                        <button onClick={handleLogout} className="logout-btn">
                            🚪 Logout
                        </button>
                    </div>
                </div>

                {/* Overlays */}
                <div
                    className={`overlay ${sidebarOpen ? 'show' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                />
                <div
                    className={`overlay modal-overlay ${requisitionModalOpen ? 'show' : ''}`}
                    onClick={() => setRequisitionModalOpen(false)}
                />

                {/* Main Content */}
                <div className="main-content">
                    {/* Top Header */}
                    <div className="top-header">
                        <div className="header-left">
                            <button
                                className="menu-toggle"
                                onClick={() => setSidebarOpen(true)}
                            >
                                ☰
                            </button>
                            <div className="header-title-section">
                                <div className="header-greeting">Requisition</div>
                                <div className="header-name">{displayName}</div>
                            </div>
                        </div>
                        <div className="header-right">
                            <div className="user-avatar-header">
                                {displayInitial}
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="content-wrapper">
                        <div className="content-area">
                            {/* Hero Section */}
                            <div className="hero-section">
                                <div className="hero-content">
                                    <h1 className="hero-title">Requisition Management 📋</h1>
                                    <p className="hero-subtitle">Submit and track your item requisition requests</p>
                                </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="stats-grid">
                                {stats.map((stat, index) => (
                                    <div key={index} className="stat-card">
                                        <div className="stat-header">
                                            <div className="stat-label">{stat.label}</div>
                                            <div className="stat-icon">{stat.icon}</div>
                                        </div>
                                        <div className="stat-value">{stat.value}</div>
                                        <div className="stat-subtext">{stat.subtext}</div>
                                    </div>
                                ))}
                            </div>

                            {/* My Requisitions */}
                            <div className="section">
                                <div className="section-header">
                                    <div className="section-title-group">
                                        <div className="section-icon">📦</div>
                                        <div className="section-title">Your Requisitions</div>
                                    </div>
                                    <button
                                        className="new-request-btn"
                                        onClick={() => setRequisitionModalOpen(true)}
                                    >
                                        <span>+</span> New Requisition
                                    </button>
                                </div>

                                {requisitions.length > 0 ? (
                                    <div className="requisitions-grid">
                                        {requisitions.map((req) => (
                                            <div key={req.id} className="requisition-card">
                                                <div className="requisition-header">
                                                    <div className="requisition-main-info">
                                                        <div className="requisition-details">
                                                            <div className="requisition-title">
                                                                {req.itemName}
                                                                <span className="requisition-category">
                                                                    {req.category}
                                                                </span>
                                                            </div>
                                                            <div className="requisition-meta">
                                                                <span>📦 Qty: {req.quantity}</span>
                                                                <span>📅 {req.requestDate}</span>
                                                                <span className={`urgency-badge urgency-${req.urgency}`}>
                                                                    {req.urgency === 'high' ? '🔴' : req.urgency === 'normal' ? '🟡' : '🟢'} {req.urgency}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div className={`requisition-status status-${req.status.toLowerCase().replace(' ', '-')}`}>
                                                        {getStatusIcon(req.status)} {req.status.replace('-', ' ')}
                                                    </div>
                                                </div>

                                                <div className="requisition-description">
                                                    {req.description}
                                                </div>

                                                {(req.approvedDate || req.remarks) && (
                                                    <div className="requisition-footer">
                                                        <div className="approval-info">
                                                            {req.approvedDate && (
                                                                <div className="approval-row">
                                                                    <span className="approval-label">Processed:</span>
                                                                    <span className="approval-value">{req.approvedDate}</span>
                                                                </div>
                                                            )}
                                                            {req.approvedBy && (
                                                                <div className="approval-row">
                                                                    <span className="approval-label">By:</span>
                                                                    <span className="approval-value">{req.approvedBy}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {req.remarks && (
                                                            <div className="remarks-box">
                                                                <div className="remarks-label">Remarks</div>
                                                                <div className="remarks-text">{req.remarks}</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-state">
                                        <div className="empty-icon">📋</div>
                                        <div className="empty-title">No Requisitions Yet</div>
                                        <div className="empty-text">
                                            You haven't submitted any requisitions yet. Click "New Requisition" to get started.
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Requisition Modal */}
                <div className={`requisition-modal ${requisitionModalOpen ? 'open' : ''}`}>
                    <div className="modal-header">
                        <div className="modal-title">New Requisition Request</div>
                        <button className="modal-close" onClick={() => setRequisitionModalOpen(false)}>
                            ✕
                        </button>
                    </div>
                    <div className="modal-content">
                        <div className="form-section">
                            <label className="form-label required">Category</label>
                            <div className="categories-grid">
                                {categories.map((category) => (
                                    <div
                                        key={category.id}
                                        className={`category-option ${selectedCategory === category.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedCategory(category.id)}
                                    >
                                        <div className="category-icon">{category.icon}</div>
                                        <div className="category-name">{category.name}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="form-section">
                            <label className="form-label required">Item Name</label>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Enter item name..."
                                value={itemName}
                                onChange={(e) => setItemName(e.target.value)}
                            />
                        </div>

                        <div className="form-section">
                            <label className="form-label required">Quantity</label>
                            <input
                                type="number"
                                className="form-input"
                                placeholder="Enter quantity..."
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                min="1"
                            />
                        </div>

                        <div className="form-section">
                            <label className="form-label">Description / Reason</label>
                            <textarea
                                className="form-textarea"
                                placeholder="Provide additional details or reason for this request..."
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </div>

                        <div className="form-section">
                            <label className="form-label">Urgency Level</label>
                            <div className="urgency-options">
                                <div
                                    className={`urgency-option ${urgency === 'low' ? 'selected' : ''}`}
                                    onClick={() => setUrgency('low')}
                                >
                                    🟢 Low
                                </div>
                                <div
                                    className={`urgency-option ${urgency === 'normal' ? 'selected' : ''}`}
                                    onClick={() => setUrgency('normal')}
                                >
                                    🟡 Normal
                                </div>
                                <div
                                    className={`urgency-option ${urgency === 'high' ? 'selected' : ''}`}
                                    onClick={() => setUrgency('high')}
                                >
                                    🔴 High
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button className="modal-btn cancel-btn" onClick={() => setRequisitionModalOpen(false)}>
                            Cancel
                        </button>
                        <button 
                            className="modal-btn submit-btn" 
                            onClick={handleSubmitRequisition}
                            disabled={submitting}
                        >
                            {submitting ? 'Submitting...' : 'Submit Request'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Requisition;